import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { RootState } from '../../../store';
import {
  filterGroupsByType,
  filterGroupsMemberNotMember,
  filterGroupsNoCoAlertNoCO,
  getIamOkGroups,
  MemberNotMemberGroupsFilters,
  searchGroupsName,
} from '../../StartIamOkMessage/helpers';
import type { GroupsFilters } from '../helpers';
import { FiltersByGroupStatus } from '../helpers';
import type { Group, GroupsState } from './types';
import { tenantScopeReset } from '../../Organizations/OrganizationsSlice/tenantScopeReset';

const initialState: GroupsState = {
  groups: [],
  documentGroups: [],
  searchedGroups: [],
  nearbyGroups: [],

  isLoading: false,
  error: null,
  joinGroupError: null,
};

const groupsSlice = createSlice({
  name: 'groupDetail',
  initialState,
  reducers: {
    setGroups: (state, action: PayloadAction<Group[]>) => {
      state.groups = action.payload;
    },
    setDocumentGroups: (state, action: PayloadAction<Group[]>) => {
      state.documentGroups = action.payload;
    },
    setGIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    resetDocumentGroups: state => {
      state.documentGroups = [];
    },
  },
  extraReducers: builder => {
    builder.addCase(tenantScopeReset, () => initialState);
  },
});

export const { setGIsLoading, setIsError, setGroups, setDocumentGroups, resetDocumentGroups } =
  groupsSlice.actions;

// data selecting could be seperated also
export const selectGroups = (state: RootState) => state.groups.groups;
export const selectGroupsIsLoading = (state: RootState) => state.groups.isLoading;
export const getGroupByID = (groupID: number, state: RootState) => {
  return state.groups.groups.find(group => {
    return group.id === groupID;
  });
};
export const selectGroupsWithFilters =
  (filters: GroupsFilters, searchTerm = '') =>
  (state: RootState) =>
    searchGroupsName(
      searchTerm,
      filterGroupsMemberNotMember(
        Array.from(filters.byGroupStatus).map(value =>
          value === FiltersByGroupStatus.Member
            ? MemberNotMemberGroupsFilters.MemberOfGroup
            : MemberNotMemberGroupsFilters.NotMemberOfGroup
        ),
        filterGroupsByType(Array.from(filters.byGroupType), state.groups.groups)
      )
    );
export const selectIamOkGroupsWithFilter =
  (filters: MemberNotMemberGroupsFilters[], searchTerm = '') =>
  (state: RootState) =>
    searchGroupsName(
      searchTerm,
      filterGroupsMemberNotMember(filters, getIamOkGroups(state.groups.groups))
    );

export const selectGroupsNoCANoCOWithFilter =
  (filters: MemberNotMemberGroupsFilters[], searchTerm = '') =>
  (state: RootState) =>
    searchGroupsName(
      searchTerm,
      filterGroupsMemberNotMember(filters, filterGroupsNoCoAlertNoCO(state.groups.groups))
    );

export const selectGroupById = (groupID: number) => (state: RootState) =>
  state.groups.groups.find(group => group.id === groupID);

export const selectGroupsByIds = (ids: number[]) => (state: RootState) =>
  state.groups.groups.filter(group => ids.includes(group.id));
// export const selectGroupUsersById = (groupID: number) => (state: RootState) =>
//   state.groups.groups.find(group => group.id === groupID

// export the reducers

export default groupsSlice.reducer;
